import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { PageSection, StatusBadge } from "../components/reporting";
import { useSession } from "../session";

interface TenantOption {
  id: string;
  name: string;
  status: string;
}

interface ManualLeadResult {
  leadId: string;
  capturedAt: string;
  deliveryRequested: boolean;
}

const inputClass =
  "w-full h-10 px-3 rounded-xs border border-default bg-surface-white text-body text-primary placeholder:text-tertiary focus:outline-none focus:border-brand-red/50 transition-colors";

function localDateTime(date = new Date()): string {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

function Field({
  label,
  htmlFor,
  hint,
  required,
  children,
  className = "",
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={htmlFor} className="text-caption font-medium text-primary">
          {label}
          {required && <span className="text-status-red"> *</span>}
        </label>
        {hint && <span className="text-micro text-tertiary">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

export function AdminManualLead() {
  const navigate = useNavigate();
  const session = useSession();
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [tenantId, setTenantId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [status, setStatus] = useState<"MQL" | "SQL">("MQL");
  const [capturedAt, setCapturedAt] = useState(() => localDateTime());
  const [campaign, setCampaign] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [practice, setPractice] = useState("");
  const [practicePostcode, setPracticePostcode] = useState("");
  const [practiceAddress, setPracticeAddress] = useState("");
  const [practiceType, setPracticeType] = useState("");
  const [enquiryNotes, setEnquiryNotes] = useState("");
  const [triggerDelivery, setTriggerDelivery] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState<ManualLeadResult | null>(null);

  useEffect(() => {
    api<{ items: TenantOption[] }>("/api/admin/tenants")
      .then((response) => {
        const available = response.items
          .filter((tenant) => ["pilot", "active"].includes(tenant.status))
          .sort((a, b) => a.name.localeCompare(b.name));
        setTenants(available);
        if (available.length === 1) setTenantId(available[0]!.id);
      })
      .catch((problem) =>
        setError(problem instanceof Error ? problem.message : "Client list is unavailable"),
      )
      .finally(() => setLoading(false));
  }, []);

  const selectedTenant = useMemo(
    () => tenants.find((tenant) => tenant.id === tenantId) ?? null,
    [tenantId, tenants],
  );

  const reset = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setStatus("MQL");
    setCapturedAt(localDateTime());
    setCampaign("");
    setJobTitle("");
    setPractice("");
    setPracticePostcode("");
    setPracticeAddress("");
    setPracticeType("");
    setEnquiryNotes("");
    setTriggerDelivery(false);
    setCreated(null);
    setError("");
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!tenantId || !capturedAt) return;
    setSaving(true);
    setError("");
    try {
      const result = await api<ManualLeadResult>(
        `/api/admin/tenants/${encodeURIComponent(tenantId)}/leads`,
        {
          method: "POST",
          body: JSON.stringify({
            firstName,
            lastName,
            email,
            phone,
            status,
            capturedAt: new Date(capturedAt).toISOString(),
            campaign,
            jobTitle,
            practice,
            practicePostcode,
            practiceAddress,
            practiceType,
            enquiryNotes,
            triggerDelivery,
          }),
        },
      );
      setCreated(result);
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : "The lead could not be added");
    } finally {
      setSaving(false);
    }
  };

  if (created) {
    return (
      <PageSection
        title="Lead added"
        right={
          <StatusBadge
            label={created.deliveryRequested ? "Delivery requested" : "Not sent"}
            tone={created.deliveryRequested ? "green" : "neutral"}
          />
        }
      >
        <div className="max-w-[720px]">
          <div className="flex items-start gap-3 rounded-md border border-status-green/30 bg-[rgba(41,166,118,0.08)] px-4 py-4">
            <i className="fa-solid fa-circle-check mt-0.5 text-status-green" aria-hidden="true" />
            <div>
              <h2 className="text-body font-semibold text-primary">
                Added to {selectedTenant?.name}
              </h2>
              <p className="mt-1 text-caption text-secondary">
                The selected date is now used in the lead list, exports and client performance
                reporting.
              </p>
              <p className="mt-1 text-caption text-secondary">
                {created.deliveryRequested
                  ? "The lead has entered the client's configured delivery flow."
                  : "No client delivery was triggered."}
              </p>
            </div>
          </div>
          <div className="mt-5 flex items-center gap-2.5">
            <button
              type="button"
              onClick={reset}
              className="h-10 px-4 rounded-xs bg-brand-red text-on-brand text-body font-semibold"
            >
              Add another lead
            </button>
            <button
              type="button"
              onClick={async () => {
                if (!tenantId) return;
                const opened = await session.enterAdminTenant(tenantId);
                if (opened) navigate(`/?lead=${encodeURIComponent(created.leadId)}`);
                else setError("Client administration could not be opened. Please try again.");
              }}
              className="h-10 px-4 rounded-xs border border-default bg-surface-white text-body font-semibold text-button"
            >
              View lead
            </button>
          </div>
          {error && <p className="mt-3 text-caption text-status-red">{error}</p>}
        </div>
      </PageSection>
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-5">
      <PageSection
        title="Client and timing"
        right={<StatusBadge label="Manual entry" tone="blue" />}
      >
        <div className="grid max-w-[860px] grid-cols-2 gap-4">
          <Field label="Client" htmlFor="manual-client" required>
            <select
              id="manual-client"
              required
              disabled={loading}
              value={tenantId}
              onChange={(event) => setTenantId(event.target.value)}
              className={inputClass}
            >
              <option value="">{loading ? "Loading clients…" : "Select a client"}</option>
              {tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Date and time added"
            htmlFor="manual-captured-at"
            hint="Used in reporting"
            required
          >
            <input
              id="manual-captured-at"
              type="datetime-local"
              required
              max={localDateTime()}
              value={capturedAt}
              onChange={(event) => setCapturedAt(event.target.value)}
              className={inputClass}
            />
          </Field>
        </div>
        <p className="mt-3 max-w-[860px] text-caption text-tertiary">
          HubSpot leads use the contact creation date when it is supplied; otherwise Leads Manager
          records the time the webhook is received. Manual leads use the date selected here.
        </p>
      </PageSection>

      <PageSection title="Lead details">
        <div className="grid max-w-[860px] grid-cols-2 gap-4">
          <Field label="First name" htmlFor="manual-first-name">
            <input
              id="manual-first-name"
              value={firstName}
              maxLength={100}
              onChange={(event) => setFirstName(event.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Last name" htmlFor="manual-last-name">
            <input
              id="manual-last-name"
              value={lastName}
              maxLength={100}
              onChange={(event) => setLastName(event.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Email" htmlFor="manual-email" required>
            <input
              id="manual-email"
              type="email"
              required
              value={email}
              maxLength={320}
              onChange={(event) => setEmail(event.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Telephone" htmlFor="manual-phone">
            <input
              id="manual-phone"
              type="tel"
              value={phone}
              maxLength={50}
              onChange={(event) => setPhone(event.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Qualification" htmlFor="manual-status" required>
            <select
              id="manual-status"
              value={status}
              onChange={(event) => setStatus(event.target.value as "MQL" | "SQL")}
              className={inputClass}
            >
              <option value="MQL">MQL</option>
              <option value="SQL">SQL</option>
            </select>
          </Field>
          <Field label="Campaign" htmlFor="manual-campaign">
            <input
              id="manual-campaign"
              value={campaign}
              maxLength={240}
              onChange={(event) => setCampaign(event.target.value)}
              className={inputClass}
            />
          </Field>
        </div>
      </PageSection>

      <PageSection title="Practice details" right={<StatusBadge label="Optional" tone="neutral" />}>
        <div className="grid max-w-[860px] grid-cols-2 gap-4">
          <Field label="Practice name" htmlFor="manual-practice">
            <input
              id="manual-practice"
              value={practice}
              maxLength={200}
              onChange={(event) => setPractice(event.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Practice postcode" htmlFor="manual-practice-postcode">
            <input
              id="manual-practice-postcode"
              value={practicePostcode}
              maxLength={20}
              onChange={(event) => setPracticePostcode(event.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Practice type" htmlFor="manual-practice-type">
            <input
              id="manual-practice-type"
              value={practiceType}
              maxLength={160}
              onChange={(event) => setPracticeType(event.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Job title" htmlFor="manual-job-title">
            <input
              id="manual-job-title"
              value={jobTitle}
              maxLength={160}
              onChange={(event) => setJobTitle(event.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="Practice address" htmlFor="manual-practice-address" className="col-span-2">
            <textarea
              id="manual-practice-address"
              rows={3}
              value={practiceAddress}
              maxLength={500}
              onChange={(event) => setPracticeAddress(event.target.value)}
              className={`${inputClass} h-auto min-h-20 py-2 resize-y`}
            />
          </Field>
          <Field label="Submitted notes" htmlFor="manual-notes" className="col-span-2">
            <textarea
              id="manual-notes"
              rows={5}
              value={enquiryNotes}
              maxLength={10_000}
              onChange={(event) => setEnquiryNotes(event.target.value)}
              className={`${inputClass} h-auto min-h-28 py-2 resize-y`}
            />
          </Field>
        </div>
      </PageSection>

      <PageSection title="Client delivery">
        <div className="max-w-[860px]">
          <label
            htmlFor="manual-trigger-delivery"
            className={`flex cursor-pointer items-start gap-3 rounded-md border px-4 py-4 transition-colors ${
              triggerDelivery
                ? "border-brand-red/35 bg-brand-red-tint"
                : "border-default bg-surface-light"
            }`}
          >
            <input
              id="manual-trigger-delivery"
              type="checkbox"
              checked={triggerDelivery}
              onChange={(event) => setTriggerDelivery(event.target.checked)}
              className="mt-1 h-4 w-4 accent-[var(--brand-red)]"
            />
            <span>
              <span className="block text-body font-semibold text-primary">
                Trigger the client's configured delivery
              </span>
              <span className="mt-1 block text-caption text-secondary">
                {triggerDelivery
                  ? "After creation, the lead will follow the client's active email and webhook delivery rules."
                  : "The lead will be visible in Leads Manager but will not be sent to any destination."}
              </span>
            </span>
          </label>
        </div>
      </PageSection>

      {error && (
        <p role="alert" className="max-w-[860px] text-caption text-status-red">
          {error}
        </p>
      )}
      <div className="flex max-w-[860px] justify-end">
        <button
          type="submit"
          disabled={saving || loading || !tenantId || !email || !capturedAt}
          className="h-10 px-5 rounded-xs bg-brand-red text-on-brand text-body font-semibold hover:bg-brand-red-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {saving ? "Adding lead…" : "Add lead"}
        </button>
      </div>
    </form>
  );
}
